import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { sortBy } from "lodash";
import moment from "moment";
import { Space, message, Row, Button, Typography, Col, Skeleton } from "antd";

import * as com from "../../Common.js";

import PDFViewer from "./PDFViewer";
import DocumentsPreviewModal from "../../Documents/PreviewModal";
import ClickablePrequalLetterDisplay from "./ClickablePrequalLetterDisplay";
import { PrequalLetterHeader } from "./PrequalLetterHeader";
import { NoPrequalLetters } from "./NoPrequalLetters";
import { SharePreQualLetterModal } from "./SharePreQualLetterModal";
import "./PrequalLetterHistory.css";
import { track } from "../../statsig";
import { STATSIG } from "../../statsig/events";

const { Text } = Typography;

// Define the structure of the data
interface PreQualLetter {
  serialID: string;
  issueDate: string;
  loanAmount: string;
  expirationDate: string;
}

const PreQualLetterHistory = forwardRef((props: { loanId?: string }, ref) => {
  let token = com.getUserToken();
  const resolveLoanId = () => {
    if (props.loanId) {
      return props.loanId;
    }
    if ((window.location.pathname || "").startsWith("/application/v2/interview")) {
      return "";
    }
    const paths = window.location.pathname.split("/");
    return paths[paths.length - 1] || "";
  };
  const loanID = resolveLoanId();

  const [data, setData] = useState<PreQualLetter[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [trigger, setTrigger] = useState<number>(0);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [pdfFile, setPdfFile] = useState<Blob | null>(null);
  const [previewNumPages, setPreviewNumPages] = useState<number>(1);
  const [previewScale, setPreviewScale] = useState<number>(1);
  const [currentLetterIndex, setCurrentLetterIndex] = useState<number>(0);
  const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const fetchData = async () => {
    try {
      // Only set loading if there are no letters, if there are letters but
      // we are refetching the letters, we should set loading to true
      if (data.length === 0) {
        setLoading(true);
      }

      const response = await fetch(
        "/borrower/get_all_prequal_letters_for_loan",
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
            Cache: "no-cache",
          },
          body: JSON.stringify({ loanID }),
        }
      );
      const rawData = await response.json();
      const sortedData = sortBy(
        rawData,
        (record) => -moment(record.issueDate).unix()
      );
      setData(sortedData);
      setLoading(false);
    } catch (error) {
      message.error("Failed to fetch data");
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    fetchData,
  }));

  useEffect(() => {
    let ignore = false;
    fetchData();
    return () => {
      ignore = true;
    };
  }, [trigger]);

  useEffect(() => {
    if (data.length > 0) {
      getPDFContent(data[currentLetterIndex].serialID);
    }
  }, [data]);

  const getPDFContent = async (documentSerialID: string) => {
    setPdfUrl("");
    setPdfFile(null);
    const response = await fetch(`/borrower/get_one_prequal_letter`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
        Cache: "no-cache",
      },
      body: JSON.stringify({ documentSerialID }),
    });

    const pdfBlob = await response.blob(); // Fetch PDF as Blob
    const pdfBlobUrl = URL.createObjectURL(pdfBlob); // Convert Blob to URL
    setPdfUrl(`${pdfBlobUrl}`); // Set Blob URL for iframe
    setPdfFile(pdfBlob);
    return pdfBlob;
  };

  const handlePreview = async (index: number) => {
    setCurrentLetterIndex(index);
    setPreviewNumPages(1);
    setPreviewScale(1);
    const loadedFile = await getPDFContent(data[index].serialID);
    if (loadedFile) {
      setIsPreviewModalOpen(true);
    }
  };

  const handleOpenSendEmailModal = () => {
    setIsSendEmailModalOpen(true);
  };

  const handleCloseSendEmailModal = () => {
    setIsSendEmailModalOpen(false);
  };

  return (
    <div style={{ padding: "20px" }}>
      <PrequalLetterHeader />
      {loading && <Skeleton active />}
      {!loading && (
        <div>
          {data.length === 0 && <NoPrequalLetters />}
          {data.length > 0 && (
            <div>
              <Row className="prequal-letter-history-subheader">
                <Col
                  span={15}
                  className="prequal-letter-history-subheader-buttons"
                >
                  <Space style={{ alignItems: "flex-start" }} size="large">
                    <a
                      href={pdfUrl}
                      download="prequal-letter.pdf" // Suggested filename for download
                      className="prequal-letter-history-download"
                      style={{ textDecoration: "none" }}
                      onClick={() => {
                        track(STATSIG.LOAN.PREQUAL_LETTER, "download", {
                          type: "download",
                        })
                      }}
                    >
                      <Button disabled={!pdfUrl}>
                        <img
                          src="/images/document-download-blue.svg"
                          alt="Download"
                        />
                        Download
                      </Button>
                    </a>
                    <Button
                      disabled={!pdfUrl}
                      onClick={handleOpenSendEmailModal}
                      style={{ display: "none" }}
                    >
                      <img src="/images/document-share-blue.svg" alt="Share" />
                      Share
                    </Button>
                  </Space>
                </Col>
                <Col span={1} className="prequal-letter-history-subheader-spacer"></Col>
                <Col
                  span={8}
                  className="prequal-letter-history-subheader-history-text"
                >
                  <Text>Pre-qual letter history</Text>
                </Col>
              </Row>

              <SharePreQualLetterModal
                visible={isSendEmailModalOpen}
                onClose={handleCloseSendEmailModal}
              />

              <Row>
                <Col 
                  span={24}
                  md={{
                    span: 8,
                    order: 3,
                  }}>
                  <div className="prequal-letter-history-selection-container">
                    {data.map((dataRow, index) => {
                      return (
                        <ClickablePrequalLetterDisplay
                          purchasePrice={Number(dataRow.loanAmount)}
                          expirationDate={dataRow.expirationDate}
                          onClick={() => {
                            getPDFContent(dataRow.serialID);
                            setCurrentLetterIndex(index);
                          }}
                          onPreview={() => handlePreview(index)}
                          active={
                            dataRow.serialID ===
                            data[currentLetterIndex].serialID
                          }
                        />
                      );
                    })}
                  </div>
                </Col>
                <Col span={24} md={{span: 1, order: 2}}></Col>
                <Col 
                  span={24}
                  xs={{ span: 0 }}
                  sm={{ span: 0 }}
                  md={{
                    span: 15,
                    order: 1,
                  }} 
                  style={{ height: "80vh" }}
                >
                  {pdfUrl ? (
                    <PDFViewer src={pdfUrl} fileName="prequal_letter.pdf" />
                  ) : (
                    <Skeleton active />
                  )}
                </Col>
              </Row>
              <DocumentsPreviewModal
                open={isPreviewModalOpen}
                onCancel={() => setIsPreviewModalOpen(false)}
                mode="preview"
                data={{ FileName: "prequal_letter.pdf", MimeType: "application/pdf" }}
                file={pdfFile}
                numPages={previewNumPages}
                width={800}
                scale={previewScale}
                handleZoom={(value: number) => setPreviewScale(value)}
                loadSuccess={({ numPages }: { numPages: number }) => setPreviewNumPages(numPages)}
                simplePreview
                previewTitle="Pre-qual letter preview"
              />
              <Row className="prequal-letter-history-download-mobile-row">
                <Col span={24}>
                    <a
                      href={pdfUrl}
                      download="prequal-letter.pdf" // Suggested filename for download
                      className="prequal-letter-history-download-mobile"
                      style={{ textDecoration: "none" }}
                      onClick={() => {
                        track(STATSIG.LOAN.PREQUAL_LETTER, "download", {
                          type: "download",
                        })
                      }}
                    >
                      <Button disabled={!pdfUrl}>
                        Download letter
                      </Button>
                    </a>
                </Col>
              </Row>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
export default PreQualLetterHistory;
